import { useMemo } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { getRandomLocation } from "../utils/mapUtils";
import adminApiClient, { MapBounds } from "../../../api/admin";

const RANDOM_OFFSET_RADIUS = 2000;
const STALE_TIME = 5 * 60 * 1000;
const CACHE_TIME = 10 * 60 * 1000;

export function useMapDevices(bounds: MapBounds) {
  const boundsKey = bounds
    ? `${bounds.northEast.lat.toFixed(4)},${bounds.northEast.lng.toFixed(4)},${bounds.southWest.lat.toFixed(4)},${bounds.southWest.lng.toFixed(4)}`
    : null;

  const {
    data: devices,
    isLoading,
    error,
    isFetching,
  } = useQuery({
    queryKey: ["devices-by-bounds", boundsKey],
    queryFn: async () => {
      return await adminApiClient.getMapDevices(bounds);
    },
    enabled: !!bounds,
    staleTime: STALE_TIME,
    gcTime: CACHE_TIME,
    placeholderData: keepPreviousData,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const devicesWithRandomLocation = useMemo(() => {
    if (!devices || !Array.isArray(devices)) return [];

    return devices
      .filter((device) => device.location && device.loopUrl)
      .map((device) => ({
        ...device,
        randomLocation: getRandomLocation(
          device.location.latitude,
          device.location.longitude,
          RANDOM_OFFSET_RADIUS,
        ),
      }));
  }, [devices]);

  return {
    devices: devicesWithRandomLocation,
    isLoading,
    isFetching,
    error: error as Error | null,
  };
}
